<template>
  <div data-testid="form-checkbox">
    <div
      class="flex"
      :class="checkboxLabelContainerCustomCssClasses">
      <input
        :id="inputId"
        class="peer absolute z-10 ms-[.5em] mt-[.5em] aspect-square h-[1.5em] cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
        :class="checkboxCustomCssClasses"
        type="checkbox"
        :value="value"
        v-model="inputBoundValue"
        :required="isRequired"
        :disabled="isDisabled"
        :aria-invalid="isInvalid"
        :aria-errormessage="errorMessageId"
        :aria-describedby="guidanceMessageId"
        :aria-controls="ariaControls"
        @change="$emit('custom-change', $event)"
        @focus="$emit('custom-focus', $event)"
        @blur="$emit('custom-blur', $event)"
      />
      <span
        aria-hidden="true"
        class="absolute ms-[.5em] mt-[.5em] inline-flex aspect-square h-[1.5em] shrink-0 items-center justify-center rounded-sm border-2 border-dark-grey bg-white text-white peer-checked:border-royal-blue-100 peer-checked:bg-royal-blue-100 peer-checked:before:block peer-checked:before:h-[1em] peer-checked:before:w-[.375em] peer-checked:before:translate-y-[-0.25em] peer-checked:before:rotate-45 peer-checked:before:shadow-[inner_.125em_.125em] peer-checked:before:shadow-current peer-checked:before:content-[''] peer-hover:border-royal-blue-160 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-royal-blue-100 peer-disabled:border-royal-blue-20 peer-disabled:bg-inherit peer-disabled:text-royal-blue-20"
        :class="[
          ...invalidStateFauxCheckboxCssClasses,
          ...fauxCheckboxCustomCssClasses,
        ]"
      >
      </span>
      <slot name="labelTextLong">
        <label
          :for="inputId"
          class="cursor-pointer py-[.5em] pe-[1em] ps-[2.5em] text-royal-blue-160 peer-disabled:cursor-not-allowed peer-disabled:text-royal-blue-20"
          :class="labelCustomCssClasses"
          data-testid="checkbox-label"
        >
          {{ labelText }}
        </label>
      </slot>
    </div>

    <div
      v-if="showGuidanceMessage"
      :id="guidanceMessageId"
      data-testid="checkbox-guidance"
    >
      <slot name="guidanceMessageLong">
        <p class="mb-[.5em] ps-[2.5em]">
          {{ guidanceMessage }}
        </p>
      </slot>
    </div>

    <div
      v-if="showErrorMessage"
      role="alert"
      :id="errorMessageId"
      data-testid="checkbox-error-message"
    >
      <slot name="errorMessageLong">
        <div class="mb-[.5em] flex gap-x-[.5em] ps-[.5em]">
          <WarningTriangleIcon
            title="Error:"
            class="shrink-0 text-ruby-red-100"
            width="1.5em"
            height="1.5em"
          />
          <p>
            {{ errorMessage }}
          </p>
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import WarningTriangleIcon from './icons/WarningTriangleIcon.vue'

/** Checkboxes enable a user to answer a yes/no binary question. They should always be paired with a label or instructions that clearly indicate what checking the box represents. */
export default {
  name: 'FormCheckbox',
  props: {
    /** The id for the input, and used to asociate with the input's label. Used also to programmatically associate with the guidance message and error message element, if present. */
    inputId: {
      type: String,
      required: true,
    },
    /** Describes the information the user wants to select or unselect */
    labelText: {
      type: String,
      default: '',
    },
    /** For accessibility (and validation if HTML5 validation api were to be used, but likely will be managed in Vue instead). Indicates whether the input must be selected.  */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /** Disables the user from editing/focusing the input. */
    isDisabled: {
      type: Boolean,
      default: false,
    },
    /** For accessibility, to communicate to screen readers that the input is currently invalid. Also used to apply css classes. */
    isInvalid: {
      type: Boolean,
      default: false,
    },
    /** For usability, to provide additional guidance to the user. Recommended to indicate whether the checkbox is required or optional here. */
    guidanceMessage: {
      type: String,
      default: '',
    },
    /** Error message shown to the user, explaining the problem and how they can correct it. Note: is configured to be shown when isInvalid property is assigned as true. Can be replaced using corresponding slot.  */
    errorMessage: {
      type: String,
      default: '',
    },
    /** The bound or associated variable that stores the checkbox state. Can be a boolean or array type. */
    modelValue: {
      type: [Boolean, Array],
    },
    /** The value that the checkbox assigns to modelValue when checked. If modelValue is an array, this prop can be used to assign a bespoke value. If this property is assigned a value that modelValue is initialised with, the checkbox will be pre-checked, negating need for a checked property. */
    value: {
      type: [Boolean, String, Number],
      default: true,
    },
    /** Tailwind CSS classes can be passed to parent container of checkbox and label for edge-case additional styling requirements. Considered an escape-hatch approach. */
    checkboxLabelContainerCustomCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Tailwind CSS classes can be passed to faux checkbox for edge-case additional styling requirements. Considered an escape-hatch approach. */
    checkboxCustomCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Tailwind CSS classes can be passed to faux checkbox for edge-case additional styling requirements. Considered an escape-hatch approach. */
    fauxCheckboxCustomCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Tailwind CSS classes can be passed to label for edge-case additional styling requirements. Considered an escape-hatch approach. Use equivalent slot for full control.  */
    labelCustomCssClasses: {
      type: Array,
      default: () => [],
    },
    /** Accessibility. If input controls another element(s), specify the controlled element(s) ids. */
    ariaControls: {
      type: String,
      default: null,
    },
  },
  computed: {
    // for v-model property binding
    inputBoundValue: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    showErrorMessage() {
      // for accessibility, only when the checkbox is invalid and an error message prop or slot has been provided
      return (
        this.isInvalid && (this.errorMessage || !!this.$slots.errorMessageLong)
      )
    },
    errorMessageId() {
      // for accessibility, generate an id for the error message, if there is one, to associate error message with the invalid input. Otherwise return undefined for vue to remove the attribute.
      return this.showErrorMessage ? `error-${this.inputId}` : undefined
    },
    showGuidanceMessage() {
      return this.guidanceMessage || !!this.$slots.guidanceMessageLong
    },
    guidanceMessageId() {
      // for accessibility, generate an id for the error message, if there is one, to associate error message with the input. Otherwise return undefined for vue to remove the attribute.
      return this.showGuidanceMessage ? `guidance-${this.inputId}` : undefined
    },
    invalidStateFauxCheckboxCssClasses() {
      // apply css classes to the input when it is invalid
      // Question: Why not use css :invalid pseudo selector/classes instead? Answer: Because for checkboxes with required attribute, :invalid styles will be applied when the checkbox is unchecked and before the user has interacted, which is not the desired behaviour for UX.
      return this.isInvalid ? ['border-ruby-red-100'] : []
    },
  },
  //
  emits: ['update:modelValue', 'custom-change', 'custom-focus', 'custom-blur'], // custom-* events available for potential input validation/error message handling
  components: {
    WarningTriangleIcon,
  },
}
</script>
